import { useCallback, useEffect, useMemo } from 'react';
import { StaffControllerApi } from '../api/generated';
import type { CreateStaffRequest, StaffResponse, UpdateStaffRequest } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  removeStaff,
  selectStaff,
  selectStaffError,
  selectStaffLoading,
  setError,
  setLoading,
  setStaff,
  upsertStaff
} from '../store/staff/staffSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseStaffResult {
  staff: StaffResponse[];
  isLoading: boolean;
  loadError: string | null;
  locationId: string | null;
  refresh: () => Promise<void>;
  createStaff: (payload: CreateStaffRequest) => Promise<StaffResponse>;
  updateStaff: (id: string, payload: UpdateStaffRequest) => Promise<StaffResponse>;
  deleteStaff: (id: string) => Promise<void>;
}

const STAFF_LOAD_ERROR = 'Failed to load staff';

export function useStaff(locationId: string | null): UseStaffResult {
  const dispatch = useAppDispatch();
  const staff = useAppSelector(selectStaff);
  const isLoading = useAppSelector(selectStaffLoading);
  const loadError = useAppSelector(selectStaffError);
  const api = useMemo(() => new StaffControllerApi(createApiConfiguration()), []);

  const loadStaffForLocation = useCallback(
    async (locId: string) => {
      dispatch(setLoading(true));
      try {
        const response = await api.listStaff({ locationId: locId });
        dispatch(setStaff(response.data));
      } catch (e) {
        dispatch(setError(getErrorMessage(e, STAFF_LOAD_ERROR)));
        throw e;
      }
    },
    [api, dispatch]
  );

  const refresh = useCallback(async () => {
    if (!locationId) {
      dispatch(setStaff([]));
      return;
    }
    await loadStaffForLocation(locationId);
  }, [dispatch, loadStaffForLocation, locationId]);

  useEffect(() => {
    if (!locationId) return;
    loadStaffForLocation(locationId).catch(() => {
      // error already surfaced via slice state
    });
  }, [locationId, loadStaffForLocation]);

  const createStaff = useCallback(
    async (payload: CreateStaffRequest) => {
      if (!locationId) throw new Error('No location available');
      const response = await api.createStaff({ locationId, createStaffRequest: payload });
      dispatch(upsertStaff(response.data));
      return response.data;
    },
    [api, dispatch, locationId]
  );

  const updateStaff = useCallback(
    async (id: string, payload: UpdateStaffRequest) => {
      if (!locationId) throw new Error('No location available');
      const response = await api.updateStaff({
        locationId,
        staffId: id,
        updateStaffRequest: payload
      });
      dispatch(upsertStaff(response.data));
      return response.data;
    },
    [api, dispatch, locationId]
  );

  const deleteStaff = useCallback(
    async (id: string) => {
      if (!locationId) throw new Error('No location available');
      await api.deleteStaff({ locationId, staffId: id });
      dispatch(removeStaff(id));
    },
    [api, dispatch, locationId]
  );

  return {
    staff,
    isLoading,
    loadError,
    locationId,
    refresh,
    createStaff,
    updateStaff,
    deleteStaff
  };
}
